import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { demarcheAPI, logementAPI, userAPI } from '../../service/api';
import { FiHome, FiFileText, FiHeart, FiMessageSquare, FiUser, FiCamera, FiArrowRight } from 'react-icons/fi';
import { resolveUploadUrl } from '../../config/config';
import { toast } from '../../service/swal';
import PageMeta from '../../components/PageMeta';
import useAutoRefresh from '../../hooks/useAutoRefresh';

export default function StudentDashboard() {
  const { user, profile, loadUser } = useAuth();
  const [steps, setSteps] = useState([]);
  const [favCount, setFavCount] = useState(0);
  const avatarInputRef = useRef(null);

  const refreshData = useCallback(async () => {
    try {
      const [stepsRes, favRes] = await Promise.all([
        demarcheAPI.getAll(),
        logementAPI.getMyFavorites(),
      ]);
      setSteps(stepsRes.data.data || []);
      setFavCount(favRes.data.data?.length || 0);
    } catch {}
  }, []);

  useEffect(() => { refreshData(); }, [refreshData]);
  useAutoRefresh(refreshData, 30000);

  const statCards = [
    { icon: FiFileText, label: 'DÉMARCHES EN COURS', value: steps.filter(s => s.statutCourant !== 'valide').length, href: '/demarches' },
    { icon: FiHeart, label: 'FAVORIS', value: favCount, href: '/favoris' },
    { icon: FiHome, label: 'LOGEMENTS DISPONIBLES', value: '50+', href: '/logements' },
    { icon: FiMessageSquare, label: 'MESSAGES', value: 0, href: '/messages' },
  ];

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      await userAPI.uploadAvatar(formData);
      await loadUser();
      toast('success', 'Photo de profil mise à jour');
    } catch (err) {
      toast('error', 'Erreur lors du changement de photo');
    }
  };

  return (
    <div className="page-container py-8">
      <PageMeta title="Tableau de bord étudiant" />
      <div className="flex items-center gap-4 mb-8">
        <div className="relative group cursor-pointer" onClick={() => avatarInputRef.current?.click()}>
          {user?.avatarUrl ? (
            <img src={resolveUploadUrl(user.avatarUrl)} alt="" className="w-14 h-14 rounded-full object-cover" />
          ) : (
            <div className="w-14 h-14 flex items-center justify-center text-xl font-bold rounded-full" style={{ backgroundColor: '#F4F4F0', color: '#0A192F' }}>
              {user?.nom?.charAt(0) || 'U'}
            </div>
          )}
          <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition">
            <FiCamera className="w-5 h-5 text-white" />
          </div>
          <input type="file" ref={avatarInputRef} className="hidden" accept="image/*" onChange={handleAvatarChange} />
        </div>
        <div>
          <h1 className="font-serif text-2xl font-bold tracking-tight" style={{ color: '#0A192F' }}>
            Bonjour, {user?.nom}
          </h1>
          <p className="text-sm" style={{ color: '#7A828A' }}>{profile?.filiere || profile?.nationalite || 'Étudiant international'}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map(({ icon: Icon, label, value, href }) => (
          <Link key={label} to={href} className="card-root p-5 hover:shadow-md transition-shadow relative">
            <Icon className="w-6 h-6 mb-2" style={{ color: '#C59B6C' }} />
            <p className="text-2xl font-bold" style={{ color: '#0A192F' }}>{value}</p>
            <p className="label-root mt-1">{label}</p>
            <FiArrowRight className="absolute bottom-4 right-4 w-4 h-4" style={{ color: '#C59B6C' }} />
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-root p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif font-bold text-base" style={{ color: '#0A192F' }}>Mes démarches</h2>
            <Link to="/demarches" className="text-[11px] font-bold tracking-widest uppercase" style={{ color: '#C59B6C' }}>Voir tout</Link>
          </div>
          {steps.length === 0 ? (
            <p className="text-sm py-8 text-center" style={{ color: '#7A828A' }}>Aucune démarche</p>
          ) : (
            <div className="space-y-3">
              {steps.slice(0, 4).map((step) => (
                <div key={step.id} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid #E5E5E0' }}>
                  <span className="text-sm font-semibold capitalize" style={{ color: '#0A192F' }}>{step.type.replace('_', ' ')}</span>
                  <span className={`text-[11px] font-bold tracking-widest uppercase px-3 py-1 border-2 ${
                    step.statutCourant === 'valide' ? 'border-green-600 text-green-600' :
                    step.statutCourant === 'rejete' ? 'border-red-600 text-red-600' :
                    step.statutCourant === 'en_cours' ? 'border-blue-600 text-blue-600' : 'border-yellow-600 text-yellow-600'
                  }`}>
                    {step.statutCourant.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card-root p-6">
          <h2 className="font-serif font-bold text-base mb-4" style={{ color: '#0A192F' }}>Profil étudiant</h2>
          <div className="space-y-3 text-sm">
            {[
              { label: 'PASSEPORT', value: profile?.numeroPasseport || '—' },
              { label: 'NATIONALITÉ', value: profile?.nationalite || '—' },
              { label: 'UNIVERSITÉ D\'ORIGINE', value: profile?.universiteOrigine || '—' },
              { label: 'FILIÈRE', value: profile?.filiere || '—' },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between py-2" style={{ borderBottom: '1px solid #E5E5E0' }}>
                <span className="label-root">{label}</span>
                <span className="font-semibold" style={{ color: '#0A192F' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
